import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useDispatch, useSelector } from 'react-redux';
import { fetchItems, postItem, putItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import { checkCiscoProductsLoaded } from '../../../support/slices/productsSlice';
import { CacheKey } from '../../../types/cache-keys';
import { parseSelectValue, unpackItems } from './escalationFormUtils';

type Product = {
  id: number;
  name: string;
};

type EscalationTypeOption = {
  id: number;
  name: string;
};

type StatusOption = {
  id: number;
  title: string;
};

type UserOption = {
  id: number;
  email: string;
};

type UserRef = {
  id: number;
  email?: string;
};

type ApprovalStatusRef = {
  id: number;
  title?: string;
};

type EscalationTypeRef = {
  id: number;
  name?: string;
};

type OpportunityRef = {
  id: string;
  customer_id?: string | null;
};

type ProductRef = {
  id: number;
  name?: string;
};

type EscalationRef = {
  id: number;
};

type AssignmentHistoryRef = {
  id: number;
  assignment_type?: string | null;
  status?: string | null;
  assigned_to?: UserRef | null;
};

type ApprovalHistoryRef = {
  id: number;
  approval_request_id?: number | null;
  status?: string | null;
  approver?: UserRef | null;
  target_assignment_type?: string | null;
  level?: number | null;
};

type EscalationHistoryItem = {
  id: number;
  escalation?: EscalationRef | null;
  event_type: string;
  previous_value?: string | null;
  new_value?: string | null;
  performed_by?: UserRef | null;
  notes?: string | null;
  created: string;
  assignment?: AssignmentHistoryRef | null;
  approval?: ApprovalHistoryRef | null;
};

export type EscalationRecord = {
  id?: number;
  opportunity?: OpportunityRef | null;
  product?: ProductRef | null;
  escalation_type?: EscalationTypeRef | null;
  status?: ApprovalStatusRef | null;
  stage?: number;
  requested_by?: UserRef | null;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical' | null;
  desired_completion_date?: string | null;
  resolved_at?: string | null;
  jira_issue_id?: string | null;
  created?: string;
  last_updated?: string;
  updated_by?: string | null;
};

interface Props {
  open: boolean;
  editItem: EscalationRecord | null;
  onClose: () => void;
  readOnly?: boolean;
}

const getNumericObjectId = (value?: { id?: number | string } | null): number | '' => {
  if (value?.id == null) return '';
  if (typeof value.id === 'number' && !Number.isNaN(value.id)) return value.id;
  if (typeof value.id === 'string') {
    if (value.id.trim() === '') return '';
    const parsed = Number(value.id);
    return Number.isNaN(parsed) ? '' : parsed;
  }
  return '';
};

const formatEventType = (value: string): string => {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const renderHistoryActor = (item: EscalationHistoryItem): string => {
  return item.performed_by?.email ?? '';
};

const renderHistoryContext = (item: EscalationHistoryItem): string => {
  const parts: string[] = [];

  if (item.assignment) {
    parts.push(`Assignment #${item.assignment.id}`);
    if (item.assignment.assignment_type) {
      parts.push(item.assignment.assignment_type);
    }
    if (item.assignment.status) {
      parts.push(`Status: ${item.assignment.status}`);
    }
  }

  if (item.approval) {
    parts.push(`Approval #${item.approval.id}`);
    if (item.approval.approval_request_id != null) {
      parts.push(`Approval Request #${item.approval.approval_request_id}`);
    }
    if (item.approval.status) {
      parts.push(`Approval Status: ${item.approval.status}`);
    }
    if (item.approval.target_assignment_type) {
      parts.push(`Target: ${item.approval.target_assignment_type}`);
    }
    if (item.approval.level != null) {
      parts.push(`Level: ${item.approval.level}`);
    }
  }

  return parts.join(' • ');
};

const EscalationEditDialog: React.FC<Props> = ({
                                                 open,
                                                 editItem,
                                                 onClose,
                                                 readOnly = false,
                                               }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const products = useSelector((state: RootState) => state.products.ciscoOnly);
  const isProductsLoading = useSelector(
    (state: RootState) => !!state.products.isCiscoProductsLoading
  );

  const [productId, setProductId] = useState<number | ''>('');
  const [escalationTypeId, setEscalationTypeId] = useState<number | ''>('');
  const [statusId, setStatusId] = useState<number | ''>('');
  const [requestedById, setRequestedById] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [desiredCompletionDate, setDesiredCompletionDate] = useState('');
  const [opportunity, setOpportunity] = useState<string>('');

  const [types, setTypes] = useState<EscalationTypeOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [historyItems, setHistoryItems] = useState<EscalationHistoryItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    dispatch(checkCiscoProductsLoaded() as never);
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    Promise.all([
      fetchItems('db/escalation-type'),
      fetchItems('db/approval-status'),
      fetchItems('db/user'),
    ])
      .then(([typesResp, statusesResp, usersResp]) => {
        const nextTypes = unpackItems<EscalationTypeOption>(typesResp);
        const nextStatuses = unpackItems<StatusOption>(statusesResp);
        const nextUsers = unpackItems<UserOption>(usersResp);

        setTypes(nextTypes);
        setStatuses(nextStatuses);
        setUsers(nextUsers);

        const nextProductId = getNumericObjectId(editItem?.product);
        const nextEscalationTypeId = getNumericObjectId(editItem?.escalation_type);
        const nextStatusId = getNumericObjectId(editItem?.status);
        const nextRequestedById = getNumericObjectId(editItem?.requested_by);

        setProductId(nextProductId);
        setEscalationTypeId(nextEscalationTypeId);
        setStatusId(nextStatusId);
        setRequestedById(nextRequestedById);

        setDescription(editItem?.description ?? '');
        setPriority(editItem?.priority ?? 'medium');
        setDesiredCompletionDate(editItem?.desired_completion_date ?? '');
        setOpportunity(editItem?.opportunity?.id != null ? String(editItem.opportunity.id) : '');
      })
      .finally(() => setLoading(false));
  }, [open, editItem]);

  useEffect(() => {
    if (!open) return;

    if (!editItem?.id) {
      setHistoryItems([]);
      return;
    }

    setHistoryLoading(true);

    fetchItems('db/escalation-history', {
      escalation: editItem.id,
      detailed: true,
      depth: 2,
    })
      .then((historyResp) => {
        setHistoryItems(unpackItems<EscalationHistoryItem>(historyResp));
      })
      .catch(() => {
        setHistoryItems([]);
      })
      .finally(() => setHistoryLoading(false));
  }, [open, editItem?.id]);

  const handleSave = async () => {
    if (productId === '' || statusId === '' || requestedById === '') {
      alert('Product, Status, and Requested By are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: editItem?.id,
        opportunity_id: opportunity || null,
        product_id: productId,
        escalation_type_id: escalationTypeId === '' ? null : escalationTypeId,
        status_id: statusId,
        requested_by_id: requestedById,
        description,
        priority,
        desired_completion_date: desiredCompletionDate || null,
      };

      if (editItem?.id) {
        await putItem('db/escalation', payload);
      } else {
        await postItem('db/escalation', payload);
      }

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalations'] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationHistory'] });
      onClose();
    } catch {
      alert('Failed to save escalation.');
    } finally {
      setSaving(false);
    }
  };

  const sortedHistory = useMemo(() => {
    return [...historyItems].sort((a, b) => {
      const aTime = new Date(a.created).getTime();
      const bTime = new Date(b.created).getTime();
      return bTime - aTime;
    });
  }, [historyItems]);

  const isBusy = loading || saving || isProductsLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{editItem?.id ? 'Edit Escalation' : 'Add Escalation'}</DialogTitle>

      <DialogContent>
        {isBusy && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        )}

        <TextField
          select
          fullWidth
          margin="normal"
          label="Product"
          value={productId}
          onChange={(e) => setProductId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
        >
          {products.map((product: Product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Escalation Type"
          value={escalationTypeId}
          onChange={(e) => setEscalationTypeId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {types.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Status"
          value={statusId}
          onChange={(e) => setStatusId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
        >
          {statuses.map((status) => (
            <MenuItem key={status.id} value={status.id}>
              {status.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Requested By"
          value={requestedById}
          onChange={(e) => setRequestedById(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.email}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          margin="normal"
          label="Opportunity"
          value={opportunity}
          onChange={(e) => setOpportunity(e.target.value)}
          disabled={readOnly || isBusy}
          helperText="Opportunity ID"
        />

        <TextField
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly || isBusy}
        />

        <TextField
          select
          fullWidth
          margin="normal"
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          disabled={readOnly || isBusy}
        >
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
        </TextField>

        <TextField
          fullWidth
          margin="normal"
          label="Desired Completion Date"
          type="date"
          value={desiredCompletionDate}
          onChange={(e) => setDesiredCompletionDate(e.target.value)}
          disabled={readOnly || isBusy}
          InputLabelProps={{ shrink: true }}
        />

        {editItem?.stage != null && (
          <TextField
            fullWidth
            margin="normal"
            label="Stage"
            value={editItem.stage}
            disabled
          />
        )}

        {editItem?.id && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box mb={2}>
              <Typography variant="h6">Escalation History</Typography>
              <Typography variant="body2" color="text.secondary">
                Audit log of status changes, approvals, assignments, notes, and other workflow events.
              </Typography>
            </Box>

            {historyLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!historyLoading && sortedHistory.length === 0 && (
              <Alert severity="info">
                No history entries found for this escalation yet.
              </Alert>
            )}

            {!historyLoading && sortedHistory.length > 0 && (
              <Stack spacing={2}>
                {sortedHistory.map((item) => {
                  const contextText = renderHistoryContext(item);

                  return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {formatEventType(item.event_type)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(item.created)}
                          </Typography>
                        </Box>

                        <Box textAlign="right">
                          {renderHistoryActor(item) && (
                            <Typography variant="body2">
                              <strong>Performed By:</strong> {renderHistoryActor(item)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {!!contextText && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Context:</strong> {contextText}
                        </Typography>
                      )}

                      {(item.previous_value || item.new_value) && (
                        <Box sx={{ mt: 1 }}>
                          {item.previous_value && (
                            <Typography variant="body2">
                              <strong>Previous:</strong> {item.previous_value}
                            </Typography>
                          )}
                          {item.new_value && (
                            <Typography variant="body2">
                              <strong>New:</strong> {item.new_value}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {item.notes && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Notes:</strong> {item.notes}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>
        {!readOnly && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isBusy || productId === '' || statusId === '' || requestedById === ''}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscalationEditDialog;